import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import { actions, assign, raise } from "xstate"
import { functionExt as Fx } from "@ssc-booking/functional-support"
import { CheckoutFlowType } from "@ssc-booking/api-contracts"
import { assertEvent, maybeEvent } from "../../../../../_common/xstate-helpers"
import { ECommerceEvent } from "../e-commerce-machine"
import { sendCheckoutFailedError, sendCheckoutValidationError } from "../e-commerce-machine-helpers"
import { ECommerceContext } from "../e-commerce-machine-context"

type Context = ECommerceContext
type Events = ECommerceEvent

const allTriggeringEvents = ["VALIDATE_CHECKOUT", "VALIDATE_COURTESY_HOLD"] as const
type AllTriggeringEvents = (typeof allTriggeringEvents)[number]
const eventToFlow: Record<AllTriggeringEvents, CheckoutFlowType> = {
    VALIDATE_CHECKOUT: "ECommerce",
    VALIDATE_COURTESY_HOLD: "CourtesyHold",
}

export const checkoutActions = {
    setGuestsInfo: actions.pure<Context, Events>((ctx, ev) => {
        assertEvent(ev, ...allTriggeringEvents)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { type, ...rest } = ev
        const flowType: CheckoutFlowType = eventToFlow[type]

        return [
            assign<Context, Events>(() => ({
                guestsInfo: { mainGuest: ev.mainGuest, otherGuests: ev.otherGuests },
                checkoutTrackingId: ev.trackingData.trackingId,
            })),
            raise({ type: "VALIDATING_CHECKOUT", ...rest, flowType }),
        ]
    }),
    setCheckoutResponse: assign<Context, Events>({
        checkoutStatus: (_, ev) => {
            assertEvent(
                ev,
                "CHECKOUT_VALIDATED",
                "CHECKOUT_STARTED",
                "CHECKOUT_COMPLETED",
                "CHECKOUT_NOT_VALIDATED",
                "CHECKOUT_FAILED",
            )
            if (ev.type === "CHECKOUT_VALIDATED") return { status: "valid", response: ev.response }
            if (ev.type === "CHECKOUT_STARTED") return { status: "started", response: ev.response }
            if (ev.type === "CHECKOUT_COMPLETED") return { status: "completed", response: ev.response }
            if (ev.type === "CHECKOUT_NOT_VALIDATED") return { status: "invalid", response: ev.response }
            if (ev.type === "CHECKOUT_FAILED") return { status: "failed", response: ev.response }
            return Fx.assertExhaustive(ev)
        },
    }),
    raiseValidationNotification: actions.pure<Context, Events>((_, ev) =>
        F.pipe(maybeEvent(ev, "CHECKOUT_NOT_VALIDATED"), O.matchW(F.constUndefined, sendCheckoutValidationError)),
    ),
    raiseFailedNotification: actions.pure<Context, Events>((_, ev) =>
        F.pipe(maybeEvent(ev, "CHECKOUT_FAILED"), O.matchW(F.constUndefined, sendCheckoutFailedError)),
    ),
    clearCheckoutResponse: assign<Context, Events>({
        checkoutStatus: (ctx, ev) => {
            if (
                (ev.type === "VALIDATE_VS_CODE" || ev.type === "RESET_VS_CODE") &&
                ctx.checkoutStatus.status == "invalid" &&
                ctx.checkoutStatus.response.errorCode == "mismatchedVsNumber"
            )
                return { status: "tobeValidated" }

            return ctx.checkoutStatus
        },
    }),
}
